import styled from "@emotion/styled";
import { Box, IconButton, Stack, Tab, Tabs } from "@mui/material";
import { EyeIcon } from "@phosphor-icons/react";
import { Button, DeleteModal } from "components/UI";
import i18next from "i18next";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCentralStore } from "stores";
import {
  useCreateStructureType,
  useDeleteStructureType,
  useDenominationHierarchy,
  useStructureTypesPaginated,
  useUpdateStructureType
} from "../api";
import StructureAvatar from "./StructureAvatar";
import StructureTypeActions from "./StructureTypeActions";
import StructureTypeFormDialog from "./StructureTypeFormDialog";
import StructuresDataGrid from "./StructuresDataGrid";
import StructuresFilters from "./StructuresFilters";

// Constants
const TABS = {
  STRUCTURES: 0,
  STRUCTURE_TYPES: 1
};

const INITIAL_FORM_DATA = {
  name: "",
  parent_id: "",
  remark: ""
};

const DownloadLink = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme.palette.primary.main};
  font-weight: 500;
  font-size: 14px;
  padding: 6px 8px;
  border: 1px solid ${({ theme }) => theme.palette.primary.main};
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const DenominationStructures = () => {
  const [currentTab, setCurrentTab] = useState(TABS.STRUCTURES);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingStructureType, setEditingStructureType] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [deletingStructureType, setDeletingStructureType] = useState(null);
  const [selectedStructureTypeId, setSelectedStructureTypeId] = useState("");
  const [selectedParentId, setSelectedParentId] = useState("");

  const { id } = useParams();
  const navigate = useNavigate();

  // Store hooks with useCallback to prevent unnecessary re-renders
  const openDeleteModal = useCentralStore(
    useCallback(state => state.openDeleteModal, [])
  );
  const setHeaderTitle = useCentralStore(
    useCallback(state => state.setHeaderTitle, [])
  );

  // API Hooks
  const {
    data: structureTypesData,
    isLoading: isLoadingStructureTypes,
    refetch: refetchStructureTypes
  } = useStructureTypesPaginated({
    denominationId: id,
    filter: {
      page: page,
      per_page: pageSize
    }
  });

  // Get filtered structures data
  const { data: structuresData, isLoading: isLoadingStructures } =
    useDenominationHierarchy({
      denominationId: id,
      parentId: selectedParentId || undefined,
      structureTypeId: selectedStructureTypeId || undefined
    });

  // Get all structures for parent filter options (without filters)
  const { data: allStructuresData } = useDenominationHierarchy({
    denominationId: id
  });

  const createStructureTypeMutation = useCreateStructureType();
  const updateStructureTypeMutation = useUpdateStructureType();
  const deleteStructureTypeMutation = useDeleteStructureType();

  // Extract unique parent types for the structure type creation dropdown
  const parentTypeOptions = useMemo(() => {
    if (!structureTypesData?.result) return [];
    return structureTypesData.result.map(type => ({
      label: type.name,
      value: type.id
    }));
  }, [structureTypesData]);

  // Filter options
  const structureTypeFilterOptions = useMemo(() => {
    if (!structureTypesData?.result) return [];
    return [
      { optionLabel: "All Structure Types", value: "", disabled: false },
      ...structureTypesData.result.map(type => ({
        optionLabel: type.name,
        value: type.id
      }))
    ];
  }, [structureTypesData]);

  const parentStructureFilterOptions = useMemo(() => {
    if (!allStructuresData?.result?.data) return [];
    return [
      { optionLabel: "All Parent Structures", value: "", disabled: false },
      ...allStructuresData.result.data.map(structure => ({
        optionLabel: structure.name,
        value: structure.id
      }))
    ];
  }, [allStructuresData]);

  // Get structure types for display
  const structureTypes = structureTypesData?.result || [];
  const totalStructureTypes = structureTypesData?.total || 0;

  useEffect(() => {
    setHeaderTitle(t("denominations.structures.title"));

    return () => {
      setHeaderTitle("");
    };
  }, [i18next.language, setHeaderTitle]);

  const { t } = useTranslation();

  // Structure columns
  const structureColumns = useMemo(
    () => [
      {
        field: "image",
        headerName: "",
        width: 60,
        sortable: false,
        filterable: false,
        renderCell: params => (
          <StructureAvatar
            imageUrl={params.row.image_url}
            name={params.row.name}
          />
        )
      },
      {
        field: "name",
        headerName: t("denominations.structures.name"),
        flex: 1,
        minWidth: 200
      },
      {
        field: "denomination_structure_type",
        headerName: t("denominations.structures.type"),
        flex: 1,
        minWidth: 120,
        valueFormatter: params => params.value?.name || "None"
      },
      {
        field: "parent",
        headerName: t("denominations.structures.parentStructure"),
        flex: 1,
        minWidth: 180,
        valueFormatter: params => params.value?.name || "None"
      },
      {
        field: "created_at",
        headerName: t("denominations.structures.createdAt"),
        flex: 1,
        minWidth: 120,
        valueFormatter: params =>
          params.value ? moment(params.value).format("DD/MM/YYYY") : "N/A"
      },
      {
        field: "actions",
        headerName: t("denominations.structures.actions"),
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: params => (
          <IconButton
            onClick={() => {
              navigate(
                `/denomination-dashboard/${id}/structures/${params.row.id}`
              );
            }}
            size="small"
          >
            <EyeIcon />
          </IconButton>
        )
      }
    ],
    [id, navigate]
  );

  // Structure types columns
  const structureTypesColumns = useMemo(
    () => [
      {
        field: "name",
        headerName: t("denominations.structures.name"),
        flex: 1,
        minWidth: 200
      },
      {
        field: "remark",
        headerName: t("denominations.structures.description"),
        flex: 1,
        minWidth: 200
      },
      {
        field: "parent",
        headerName: t("denominations.structures.parentType"),
        flex: 1,
        minWidth: 120,
        valueFormatter: params => params.value?.name || "None"
      },
      {
        field: "created_at",
        headerName: t("denominations.structures.createdAt"),
        flex: 1,
        minWidth: 120,
        valueFormatter: params => moment(params.value).format("DD/MM/YYYY")
      },
      {
        field: "actions",
        headerName: t("denominations.structures.actions"),
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: params => (
          <StructureTypeActions
            onEdit={() => handleEditStructureType(params.row)}
            onDelete={() => handleDeleteStructureType(params.row)}
          />
        )
      }
    ],
    []
  );

  // Memoized handlers
  const handleEditStructureType = useCallback(structureType => {
    setEditingStructureType(structureType);
    setFormData({
      name: structureType.name,
      parent_id: structureType.parent_id || "",
      remark: structureType.remark || ""
    });
    setEditDialogOpen(true);
  }, []);

  const handleDeleteStructureType = useCallback(
    structureType => {
      setDeletingStructureType(structureType);
      openDeleteModal();
    },
    [openDeleteModal]
  );

  const handleTabChange = useCallback((event, newValue) => {
    setCurrentTab(newValue);
  }, []);

  const handleCreateStructureType = useCallback(() => {
    setCreateDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setCreateDialogOpen(false);
    setFormData(INITIAL_FORM_DATA);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setEditDialogOpen(false);
    setEditingStructureType(null);
    setFormData(INITIAL_FORM_DATA);
  }, []);

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleSubmitStructureType = useCallback(() => {
    const structureTypeData = {
      name: formData.name,
      remark: formData.remark,
      parent_id: formData.parent_id || null
    };

    if (editingStructureType) {
      updateStructureTypeMutation.mutate(
        {
          denominationId: id,
          structureTypeId: editingStructureType.id,
          structureTypeData
        },
        {
          onSuccess: () => {
            handleCloseEditDialog();
            refetchStructureTypes();
          }
        }
      );
    } else {
      createStructureTypeMutation.mutate(
        {
          denominationId: id,
          structureTypeData
        },
        {
          onSuccess: () => {
            handleCloseDialog();
            refetchStructureTypes();
          }
        }
      );
    }
  }, [
    editingStructureType,
    formData,
    id,
    updateStructureTypeMutation,
    createStructureTypeMutation,
    handleCloseEditDialog,
    handleCloseDialog,
    refetchStructureTypes
  ]);

  const handleDeleteConfirm = useCallback(() => {
    deleteStructureTypeMutation.mutate(
      {
        denominationId: id,
        structureTypeId: deletingStructureType?.id
      },
      {
        onSuccess: () => {
          refetchStructureTypes();
        }
      }
    );
  }, [
    deleteStructureTypeMutation,
    id,
    deletingStructureType,
    refetchStructureTypes
  ]);

  const handleNavigateToCreate = useCallback(() => {
    navigate(`/denomination-dashboard/${id}/structures/create`);
  }, [navigate, id]);

  // Filter handlers
  const handleStructureTypeChange = useCallback(e => {
    setSelectedStructureTypeId(e.target.value);
  }, []);

  const handleParentIdChange = useCallback(e => {
    setSelectedParentId(e.target.value);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedStructureTypeId("");
    setSelectedParentId("");
  }, []);

  // Memoized data
  const structures = useMemo(
    () => structuresData?.result?.data || [],
    [structuresData]
  );

  const totalStructures = useMemo(
    () => structuresData?.result?.total || 0,
    [structuresData]
  );

  // Pagination handlers
  const handlePageChange = useCallback(newPage => {
    setPage(newPage + 1);
  }, []);

  const handlePageSizeChange = useCallback(newPageSize => {
    setPageSize(newPageSize);
  }, []);

  // Effects
  useEffect(() => {
    setHeaderTitle(t("denominations.structures.title"));
    return () => setHeaderTitle("");
  }, [i18next.language, setHeaderTitle]);

  // Computed values
  const isDialogOpen = createDialogOpen || editDialogOpen;
  const isLoading =
    createStructureTypeMutation.isLoading ||
    updateStructureTypeMutation.isLoading;
  const mutationError =
    createStructureTypeMutation.error || updateStructureTypeMutation.error;

  const structuresPaginationProps = {
    paginationMode: "server",
    page: page - 1,
    pageSize,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    rowCount: totalStructures
  };

  const structureTypesPaginationProps = {
    paginationMode: "server",
    page: page - 1,
    pageSize,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    rowCount: totalStructureTypes
  };

  return (
    <Stack py={3} spacing={3}>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          <Tab label={t("denominations.structures.structures")} />
          <Tab label={t("denominations.structures.structureTypes")} />
        </Tabs>
      </Box>

      {/* Action Buttons */}
      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        {currentTab === TABS.STRUCTURES ? (
          <>
            <DownloadLink
              href="/forms/structure-form.pdf"
              download="Structure Form"
            >
              {t("denominations.structures.downloadStructureForm")}
            </DownloadLink>
            <Button onClick={handleNavigateToCreate}>
              {t("denominations.structures.createStructure")}
            </Button>
          </>
        ) : (
          <Button onClick={handleCreateStructureType}>
            {t("denominations.structures.createStructureType")}
          </Button>
        )}
      </Stack>

      {/* Tab Content */}
      {currentTab === TABS.STRUCTURES ? (
        <>
          {/* Filters Section */}
          <StructuresFilters
            structureTypeFilterOptions={structureTypeFilterOptions}
            parentStructureFilterOptions={parentStructureFilterOptions}
            selectedStructureTypeId={selectedStructureTypeId}
            selectedParentId={selectedParentId}
            onStructureTypeChange={handleStructureTypeChange}
            onParentIdChange={handleParentIdChange}
            onClearFilters={handleClearFilters}
          />

          <StructuresDataGrid
            data={structures}
            loading={isLoadingStructures}
            columns={structureColumns}
            paginationProps={structuresPaginationProps}
          />
        </>
      ) : (
        <StructuresDataGrid
          data={structureTypes}
          loading={isLoadingStructureTypes}
          columns={structureTypesColumns}
          paginationProps={structureTypesPaginationProps}
        />
      )}

      {/* Structure Type Form Dialog */}
      <StructureTypeFormDialog
        open={isDialogOpen}
        editingStructureType={editingStructureType}
        formData={formData}
        parentTypeOptions={parentTypeOptions}
        onClose={editDialogOpen ? handleCloseEditDialog : handleCloseDialog}
        onFormChange={handleFormChange}
        onSubmit={handleSubmitStructureType}
        isLoading={isLoading}
        error={mutationError}
      />

      {/* Delete Modal */}
      <DeleteModal
        title={t("denominations.structures.deleteStructureType")}
        name={deletingStructureType?.name}
        onDelete={handleDeleteConfirm}
      />
    </Stack>
  );
};
